'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { apiGet, API_BASE, clearTokens, getAccessToken, setTokens } from '@/lib/api';

export interface AuthUser {
  id: string;
  tenantId: string;
  name: string;
  email: string;
  username?: string | null;
  active: boolean;
  permissions?: string[];
  roles?: { id: string; name: string }[];
  stores?: { id: string; name: string }[];
  profilePhotoPath?: string | null;
}

export interface SignupInput {
  name: string;
  email: string;
  password: string;
  tenantName: string;
  tenantSlug?: string;
  storeName?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string, tenantSlug?: string) => Promise<{ ok: boolean; message?: string }>;
  signup: (data: SignupInput) => Promise<{ ok: boolean; message?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (permission: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  const refreshUser = useCallback(async () => {
    const res = await apiGet<AuthUser>('/auth/me');
    if (res.data) setUser(res.data as AuthUser);
    else setUser(null);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!getAccessToken()) {
        if (mounted) {
          setUser(null);
          setLoading(false);
        }
        return;
      }
      const res = await apiGet<AuthUser>('/auth/me');
      if (mounted) {
        if (res.data) setUser(res.data as AuthUser);
        else setUser(null);
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const login = useCallback(
    async (email: string, password: string, tenantSlug?: string) => {
      const body: { email: string; password: string; tenantSlug?: string } = { email, password };
      if (tenantSlug) body.tenantSlug = tenantSlug;
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) {
        return { ok: false, message: json.message ?? 'Login failed' };
      }
      const data = json.data ?? json;
      const access = data.accessToken ?? json.accessToken;
      const refresh = data.refreshToken ?? json.refreshToken;
      const userData = data.user ?? json.user;
      if (access && userData) {
        setTokens(access, refresh ?? '');
        setUser(userData);
        return { ok: true };
      }
      return { ok: false, message: 'Invalid response' };
    },
    [],
  );

  const signup = useCallback(async (data: SignupInput) => {
    const res = await fetch(`${API_BASE}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: data.name,
        email: data.email,
        password: data.password,
        tenantName: data.tenantName,
        ...(data.tenantSlug && { tenantSlug: data.tenantSlug }),
        ...(data.storeName && { storeName: data.storeName }),
      }),
    });
    const json = await res.json();
    if (!res.ok) {
      return { ok: false, message: json.message ?? 'Signup failed' };
    }
    const responseData = json.data ?? json;
    const access = responseData.accessToken ?? json.accessToken;
    const refresh = responseData.refreshToken ?? json.refreshToken;
    const userData = responseData.user ?? json.user;
    if (access && userData) {
      setTokens(access, refresh ?? '');
      setUser(userData);
      return { ok: true };
    }
    return { ok: false, message: 'Invalid response' };
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify({ refreshToken: typeof window !== 'undefined' ? localStorage.getItem('refreshToken') ?? '' : '' }),
      });
    } catch {
      // ignore
    }
    clearTokens();
    setUser(null);
    router.push('/login');
  }, [router]);

  const hasPermission = useCallback(
    (permission: string | string[]) => {
      if (!user?.permissions) return false;
      const list = Array.isArray(permission) ? permission : [permission];
      if (list.includes('all')) return true;
      return list.some((p) => user.permissions!.includes(p));
    },
    [user]
  );

  const value: AuthContextValue = {
    user,
    loading,
    login,
    signup,
    logout,
    refreshUser,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
